import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles, X, Send, Plus, History, ArrowLeft, Trash2,
  Archive, ArchiveRestore, AlertTriangle, Loader2, Flag,
} from "lucide-react";
import merchantApi from "../../../lib/merchantApi";
// Brief 3.6 Phase B5/B6 — merchant flag uses the same axios instance as
// the rest of the chat path (Bearer header set by merchantApi).
const api = merchantApi;

/**
 * Brief 3.3 — Merchant in-app chat with Nerea.
 *
 * Floating right-side panel + bottom-right launcher. Available on every
 * authenticated `/app/*` page via `<AppShell>`. Talks to the same brain
 * the ops DM uses, but with `Channel.MERCHANT_INAPP` (handled server-side)
 * so the merchant never sees `neptune_internal` retrieval content.
 */

const SUGGESTED_PROMPTS = [
  "How do I authenticate against PayTech?",
  "How do I verify webhook signatures?",
  "What status codes does the gateway return?",
  "Show me sandbox test cards for 3DS flows.",
];

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function NereaChat({ open, onOpen, onClose }) {
  const [view, setView] = useState("chat"); // 'chat' | 'history'
  const [archivedView, setArchivedView] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const streamRef = useRef(null);

  // ------------ Data ------------

  const loadThreads = useCallback(async (archived = false) => {
    try {
      const r = await merchantApi.get(`/merchant/chat/threads`, {
        params: { archived: archived ? "true" : undefined },
      });
      setThreads(r.data.items || []);
      return r.data.items || [];
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load conversations.");
      return [];
    }
  }, []);

  const loadThread = useCallback(async (tid) => {
    if (!tid) return;
    setLoading(true);
    try {
      const r = await merchantApi.get(`/merchant/chat/threads/${tid}`);
      setMessages(r.data.messages || []);
      setActiveTid(tid);
      setView("chat");
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load that conversation.");
    } finally {
      setLoading(false);
    }
  }, []);

  const newThread = useCallback(async () => {
    try {
      const r = await merchantApi.post(`/merchant/chat/threads`, {});
      const t = r.data;
      setThreads((prev) => [t, ...prev]);
      setActiveTid(t.thread_id);
      setMessages([]);
      setView("chat");
      setError(null);
      return t.thread_id;
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start a new conversation.");
      return null;
    }
  }, []);

  const send = useCallback(async (text) => {
    const content = (text ?? draft).trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    let tid = activeTid;
    if (!tid) {
      tid = await newThread();
      if (!tid) { setSending(false); return; }
    }
    // Optimistic user bubble
    const optimistic = {
      message_id: `optimistic-${Date.now()}`,
      role: "user", content, created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    try {
      const r = await merchantApi.post(
        `/merchant/chat/threads/${tid}/messages`, { content },
      );
      const um = r.data.user_message;
      const am = r.data.assistant_message;
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== optimistic.message_id),
        um, am,
      ]);
      // Refresh thread title in the rail
      loadThreads(archivedView);
    } catch (e) {
      // Roll back optimistic, surface error
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      setDraft(content); // give the merchant their text back
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setError(detail || "You've hit the safety ceiling. Try again in a few minutes.");
      } else if (status === 422) {
        setError(detail || "That message couldn't be sent.");
      } else {
        setError(detail || "Couldn't reach Nerea. Please try again in a moment.");
      }
    } finally {
      setSending(false);
    }
  }, [draft, sending, activeTid, newThread, loadThreads, archivedView]);

  const archiveThread = useCallback(async (tid) => {
    try {
      await merchantApi.delete(`/merchant/chat/threads/${tid}`);
      if (activeTid === tid) {
        setActiveTid(null);
        setMessages([]);
      }
      await loadThreads(archivedView);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't archive that conversation.");
    }
  }, [activeTid, archivedView, loadThreads]);

  // ------------ Effects ------------

  // Load threads on first open + auto-select the most recent non-archived.
  useEffect(() => {
    if (!open) return;
    (async () => {
      const items = await loadThreads(false);
      if (items.length > 0 && !activeTid) {
        loadThread(items[0].thread_id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reload threads when toggling archived view.
  useEffect(() => { if (open) loadThreads(archivedView); }, [archivedView, open, loadThreads]);

  // ------------ Render ------------

  const activeThread = useMemo(
    () => threads.find((t) => t.thread_id === activeTid),
    [threads, activeTid],
  );

  return (
    <>
      {/* Floating launcher — visible only when panel is closed */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="nerea-launcher"
            type="button"
            data-testid="nerea-chat-launcher"
            onClick={onOpen}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            // Placement: bottom-LEFT on mobile (Bug 5 — finger-tap collision
            // with right-anchored Continue/Submit CTAs is worst on small
            // viewports). Bottom-right on desktop, but offset upward to clear
            // any fixed action bar at the bottom-right of pages like
            // /app/onboarding/step/*.
            className="fixed left-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-24 z-[80] group inline-flex items-center gap-2 rounded-full
                       bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-black
                       px-4 py-3 shadow-[0_8px_30px_rgba(0,229,255,0.25)]
                       hover:shadow-[0_10px_40px_rgba(0,229,255,0.35)]
                       hover:-translate-y-0.5 transition-all duration-200"
            aria-label="Open Nerea chat"
          >
            <Sparkles className="h-4 w-4" />
            <span className="text-[13px] font-semibold tracking-tight">Ask Nerea</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Side panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="nerea-backdrop"
              data-testid="nerea-chat-backdrop"
              className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm md:bg-transparent md:backdrop-blur-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <motion.aside
              key="nerea-panel"
              data-testid="nerea-chat-panel"
              role="dialog"
              aria-label="Chat with Nerea"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 z-[90] h-full w-full sm:w-[440px]
                         border-l border-white/[0.08]
                         bg-gradient-to-b from-[#0a0a0a] via-[#080812] to-[#0a0a0a]
                         backdrop-blur-2xl flex flex-col"
            >
              {/* Header */}
              <header className="flex items-center justify-between gap-2 px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  {view === "history" && (
                    <button
                      type="button"
                      data-testid="nerea-chat-back-to-chat"
                      onClick={() => setView("chat")}
                      className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                      aria-label="Back to current conversation"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                  )}
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]">
                    <Sparkles className="h-3.5 w-3.5" />
                    Nerea
                  </div>
                  {view === "chat" && activeThread && (
                    <span className="ml-2 truncate text-[13px] text-zinc-300" data-testid="nerea-chat-thread-title">
                      {activeThread.title}
                    </span>
                  )}
                  {view === "history" && (
                    <span className="ml-2 text-[13px] text-zinc-300">
                      {archivedView ? "Archived" : "Conversations"}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {view === "chat" && (
                    <>
                      <button
                        type="button"
                        data-testid="nerea-chat-history-btn"
                        onClick={() => { setView("history"); setArchivedView(false); loadThreads(false); }}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                        aria-label="Show conversations"
                        title="Conversations"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid="nerea-chat-new-btn"
                        onClick={newThread}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                        aria-label="New conversation"
                        title="New conversation"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    data-testid="nerea-chat-close"
                    onClick={onClose}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </header>

              {/* Body */}
              {view === "history" ? (
                <ThreadHistory
                  threads={threads}
                  activeTid={activeTid}
                  archivedView={archivedView}
                  onToggleArchived={() => setArchivedView((v) => !v)}
                  onSelect={loadThread}
                  onArchive={archiveThread}
                />
              ) : (
                <ChatStream
                  streamRef={streamRef}
                  loading={loading}
                  messages={messages}
                  sending={sending}
                  error={error}
                  draft={draft}
                  onDraftChange={setDraft}
                  onSend={send}
                  onSuggested={(p) => send(p)}
                  hasActive={Boolean(activeTid)}
                  activeTid={activeTid}
                  loadThreads={loadThreads}
                />
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------- Subcomponents ----------

function ChatStream({
  streamRef, loading, messages, sending, error, draft,
  onDraftChange, onSend, onSuggested, hasActive,
  activeTid, loadThreads,
}) {
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  const empty = !loading && messages.length === 0;

  return (
    <>
      <div
        ref={streamRef}
        data-testid="nerea-chat-stream"
        className="flex-1 overflow-y-auto px-5 py-6 space-y-4"
      >
        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-[13px]">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        )}
        {empty && (
          <div data-testid="nerea-chat-empty" className="space-y-5 mt-2">
            <div>
              <h3 className="font-display text-xl text-white tracking-tight">
                {hasActive ? "How can Nerea help?" : "Ask Nerea anything."}
              </h3>
              <p className="mt-2 text-[13px] text-zinc-500 leading-relaxed">
                Integration questions, gateway behaviour, webhook signatures —
                Nerea has the PayTech manual indexed. For account-level changes,
                your account exec is on Telegram.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`nerea-chat-suggested-${i}`}
                  onClick={() => onSuggested(p)}
                  className="text-left text-[13px] text-zinc-300 px-4 py-3 rounded-xl
                             border border-white/[0.06] bg-white/[0.02]
                             hover:bg-white/[0.05] hover:border-white/[0.12]
                             transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.message_id}
            msg={m}
            threadId={activeTid}
            onFlagged={() => {
              // After flagging, refresh the thread so we pick up handed_off_at
              // and stop expecting a Nerea reply for new sends.
              if (activeTid) loadThreads();
            }}
          />
        ))}
        {sending && (
          <div data-testid="nerea-chat-typing" className="flex items-center gap-2 text-[12px] text-zinc-500 pl-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Nerea is thinking…
          </div>
        )}
      </div>

      {error && (
        <div
          data-testid="nerea-chat-error"
          className="mx-5 mb-2 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2.5 text-[12.5px] text-amber-200"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div className="leading-relaxed">{error}</div>
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-white/[0.06] px-4 py-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="nerea-chat-composer"
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            maxLength={4000}
            placeholder="Ask Nerea about PayTech, webhooks, or anything technical…"
            className="flex-1 resize-none max-h-32 min-h-[40px] px-3 py-2.5 rounded-xl
                       bg-white/[0.04] border border-white/[0.08]
                       focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.06]
                       text-[13.5px] text-white placeholder:text-zinc-600 transition-colors"
            disabled={sending}
          />
          <button
            type="button"
            data-testid="nerea-chat-send"
            onClick={() => onSend()}
            disabled={sending || !draft.trim()}
            className="h-10 w-10 rounded-xl flex items-center justify-center
                       bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-black
                       disabled:opacity-40 disabled:cursor-not-allowed
                       hover:shadow-[0_4px_18px_rgba(0,229,255,0.35)] transition-all"
            aria-label="Send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-1.5 px-1 text-[10.5px] uppercase tracking-[0.18em] text-zinc-600">
          Enter to send · Shift+Enter for newline
        </div>
      </div>
    </>
  );
}

function MessageBubble({ msg, threadId, onFlagged }) {
  const isUser = msg.role === "user";
  const abstained = msg.abstained;
  return (
    <div
      data-testid={`nerea-chat-msg-${msg.role}`}
      className={`flex ${isUser ? "justify-end" : "justify-start"} group`}
    >
      <div
        className={[
          "max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap",
          isUser
            ? "bg-white text-black rounded-br-md"
            : abstained
              ? "border border-amber-500/30 bg-amber-500/[0.06] text-amber-100 rounded-bl-md"
              : "border border-white/[0.08] bg-white/[0.04] text-zinc-100 rounded-bl-md",
        ].join(" ")}
      >
        {abstained && (
          <div className="mb-1.5 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-amber-300">
            <AlertTriangle className="h-3 w-3" />
            Escalation suggested
          </div>
        )}
        <div>{msg.content}</div>
        {msg.citations && msg.citations.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
            {msg.citations.map((c, i) => (
              <div
                key={c.url || c.source || c.title || `cit-${i}`}
                className="text-[11px] text-zinc-400"
              >
                <span className="text-[#00E5FF]">↗</span> {c.source || c.title || c.url || "citation"}
                {c.page_label ? ` · p. ${c.page_label}` : ""}
              </div>
            ))}
          </div>
        )}
        <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${isUser ? "text-black/50" : "text-zinc-600"}`}>
          <span>{formatTime(msg.created_at)}</span>
          {!isUser && threadId && onFlagged && (
            <FlagButton threadId={threadId} messageId={msg.message_id} onFlagged={onFlagged} />
          )}
        </div>
      </div>
    </div>
  );
}

function FlagButton({ threadId, messageId, onFlagged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("needs_human");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(
        `/merchant/chat/threads/${threadId}/messages/${messageId}/flag`,
        { reason, note: note.trim() || null },
      );
      setDone(true);
      if (onFlagged) onFlagged();
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <span
        data-testid={`nerea-flag-done-${messageId}`}
        className="text-[10.5px] text-emerald-300"
      >
        Sent to your account manager.
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`nerea-flag-btn-${messageId}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1 text-[10.5px]"
        title="Flag this answer + escalate to a human"
      >
        <Flag className="h-2.5 w-2.5" /> Flag
      </button>
      {open && (
        <div
          data-testid={`nerea-flag-modal-${messageId}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !submitting && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#111] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Flag this answer</div>
            <h3 className="mt-1 text-[15px] font-semibold text-white">Send to a human</h3>
            <p className="mt-2 text-[12.5px] text-zinc-400 leading-relaxed">
              Nerea will stop replying in this conversation. Your account manager
              will pick it up.
            </p>
            <div className="mt-4 space-y-2">
              {[
                { v: "incorrect", label: "Nerea got this wrong" },
                { v: "not_helpful", label: "This wasn't helpful" },
                { v: "needs_human", label: "I need to talk to a person" },
              ].map((opt) => (
                <label
                  key={opt.v}
                  data-testid={`nerea-flag-reason-${opt.v}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12.5px] cursor-pointer transition-colors ${
                    reason === opt.v
                      ? "border-rose-400/40 bg-rose-400/[0.06] text-white"
                      : "border-white/[0.08] bg-white/[0.02] text-zinc-300 hover:bg-white/[0.04]"
                  }`}
                >
                  <input
                    type="radio" name="flag-reason"
                    checked={reason === opt.v}
                    onChange={() => setReason(opt.v)}
                    className="accent-rose-400"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <textarea
              data-testid={`nerea-flag-note-${messageId}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for your account manager (optional)"
              maxLength={500}
              className="mt-3 w-full rounded-md border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-rose-400/40 resize-none"
              rows={3}
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setOpen(false)}
                data-testid={`nerea-flag-cancel-${messageId}`}
                className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={submit}
                data-testid={`nerea-flag-submit-${messageId}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/80 hover:bg-rose-500 text-white px-4 py-1.5 text-[12px] font-medium"
              >
                {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Flag className="h-3 w-3" />}
                Send to human
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ThreadHistory({
  threads, activeTid, archivedView, onToggleArchived, onSelect, onArchive,
}) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-5 py-3 flex items-center justify-between border-b border-white/[0.04]">
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          {threads.length} {archivedView ? "archived" : "active"}
        </span>
        <button
          type="button"
          data-testid="nerea-chat-archived-toggle"
          onClick={onToggleArchived}
          className="text-[11.5px] text-zinc-400 hover:text-white inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.04] transition-colors"
        >
          {archivedView ? (
            <><ArchiveRestore className="h-3.5 w-3.5" /> View active</>
          ) : (
            <><Archive className="h-3.5 w-3.5" /> View archived</>
          )}
        </button>
      </div>

      {threads.length === 0 ? (
        <div className="px-5 py-12 text-center text-[12.5px] text-zinc-500">
          {archivedView ? "Nothing archived yet." : "No conversations yet — start a new one."}
        </div>
      ) : (
        <ul className="divide-y divide-white/[0.04]">
          {threads.map((t) => {
            const isActive = t.thread_id === activeTid && !archivedView;
            return (
              <li
                key={t.thread_id}
                data-testid={`nerea-chat-thread-row-${t.thread_id}`}
                className={`group flex items-center gap-2 px-5 py-3 hover:bg-white/[0.03] transition-colors ${isActive ? "bg-white/[0.04]" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => onSelect(t.thread_id)}
                  data-testid={`nerea-chat-thread-open-${t.thread_id}`}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-[13px] text-white truncate">{t.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {t.message_count} messages
                    {t.last_message_at ? ` · ${new Date(t.last_message_at).toLocaleDateString()}` : ""}
                  </div>
                </button>
                {!archivedView && (
                  <button
                    type="button"
                    data-testid={`nerea-chat-thread-archive-${t.thread_id}`}
                    onClick={() => onArchive(t.thread_id)}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 group-focus-within:opacity-100 h-7 w-7 rounded-md flex items-center justify-center text-zinc-500 hover:text-rose-300 hover:bg-rose-500/[0.08] transition-all"
                    aria-label="Archive conversation"
                    title="Archive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
